import {
  configureStore,
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
} from '@reduxjs/toolkit';
import { backofficeApi, parseError } from './api';
import authReducer from './auth-slice';

// Register libs/api endpoints with mehirApi (= backofficeApi)
import '@mehir/api/backoffice-api';
import '@mehir/api/campaign-api';
import '@mehir/api/contract-api';
import '@mehir/api/blog-api';
import '@mehir/api/fit-api';
import '@mehir/api/finance-api';
import '@mehir/api/auth-api';

// Apply cache tags (providesTags/invalidatesTags) — must run after the
// endpoint modules above are registered. The libs/api barrel (@mehir/api)
// is NOT imported here (it pulls in React Native code), so each tag-enhancer
// file must be wired explicitly. Only the backoffice one used to be, which
// left every other service without invalidation and forced screens to patch
// over it with manual refetch() calls.
import '@mehir/api/backoffice-api/backoffice-api-tags';
import '@mehir/api/auth-api/auth-api-tags';
import '@mehir/api/contract-api/contract-api-tags';
import '@mehir/api/campaign-api/campaign-api-tags';
import '@mehir/api/blog-api/blog-api-tags';
import '@mehir/api/fit-api/fit-api-tags';
import '@mehir/api/finance-api/finance-tags';

// Tags for the endpoints only this app calls — the libs/api tag files cover
// the mobile surface. Must come after the endpoint modules above.
import './cache-tags';
import './stats-api';

import { logout } from './auth-slice';
import { setSessionExpiredHandler, tokenStore } from './api-config';

// Toast function will be injected when needed
let showToast: ((message: string, title?: string) => void) | null = null;

export const setToastFunction = (toastFn: (message: string, title?: string) => void) => {
  showToast = toastFn;
};

/**
 * Tears down every trace of the current session: stored tokens, auth state and
 * the RTK Query cache. Without the cache reset the next user to sign in on the
 * same browser sees the previous user's cached records.
 */
export const endSession = () => {
  tokenStore.clear();
  store.dispatch(logout());
  store.dispatch(backofficeApi.util.resetApiState());
};

export const rtkQueryErrorLogger: Middleware =
  (_api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      const parsed = parseError(action.payload);
      console.error('[api]', parsed, action.payload);

      // 404 is handled by the screens themselves — every list and detail view
      // renders its own empty/error state, and every mutation already reports
      // its own failure — so a global toast for it was pure noise.
      switch (parsed.kind) {
        case 'unauthorized':
          showToast?.('Oturum süresi doldu. Lütfen tekrar giriş yapın.', 'Yetkilendirme Hatası');
          // A 401 that reaches here is terminal: prepareHeaders already tries
          // to refresh an expiring token before every request.
          endSession();
          break;
        case 'forbidden':
          showToast?.('Bu işlem için yetkiniz bulunmamaktadır.', 'Yetki Hatası');
          break;
        case 'validation':
          // Previously not handled at all, so a rejected request looked like
          // nothing had happened. This backend also answers 400 for its own
          // unhandled exceptions, hence the fallback wording.
          showToast?.(
            parsed.message || 'İstek sunucu tarafından reddedildi.',
            'Geçersiz İstek'
          );
          break;
        case 'network':
          showToast?.(
            'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.',
            'Bağlantı Hatası'
          );
          break;
        case 'server':
          showToast?.(parsed.message || 'Bir hata oluştu. Lütfen tekrar deneyin.', 'Sunucu Hatası');
          break;
        default:
          break;
      }
    }

    return next(action);
  };

// The auth slice is intentionally NOT persisted. It used to be, which gave the
// session two sources of truth: redux-persist kept `token`/`isAuthenticated` on
// disk while the 401 handler only cleared localStorage, so a reload could come
// back "signed in" with no token to send and loop on 401s. localStorage (via
// tokenStore) is now the only place a token lives; redux rehydrates from it on
// boot in useAuth.
export const store = configureStore({
  devTools: process.env.NODE_ENV !== 'production',
  reducer: {
    [backofficeApi.reducerPath]: backofficeApi.reducer,
    auth: authReducer,
  },
  middleware: (get) => get().concat(backofficeApi.middleware, rtkQueryErrorLogger),
});

// A failed token refresh happens outside redux, so let it end the session too.
setSessionExpiredHandler(endSession);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Re-export API hooks and types for convenience
export * from './api';
